import React, { type ReactElement } from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/img/landing/logo.webp";

const DISCORD_URL =
  process.env.REACT_APP_DISCORD_URL || "#";

export type JoinCommunityAndFooterProps = {
  /**
   * Footer primary link: Home shows FAQ first; all other pages show Home first.
   * `privacy` / `terms` omit the current page from the Privacy / Terms pair.
   */
  variant: "home" | "faq" | "privacy" | "terms";
  /** Gold ornament above social (not shown when `showSocialSection` is false). */
  showOrnamentDivider?: boolean;
  /** “Join the Community” + social buttons — off for legal pages that only need the footer. */
  showSocialSection?: boolean;
};

/**
 * “Join the Community” + legal footer — shared by Home, FAQ, and legal pages (landing.css `.land-home` rules).
 */
const JoinCommunityAndFooter = ({
  variant,
  showOrnamentDivider = true,
  showSocialSection = true,
}: JoinCommunityAndFooterProps): ReactElement => {
  const showOrnament = showOrnamentDivider && showSocialSection;

  return (
    <>
      {showOrnament ? (
        <div className="ornament-divider">
          <span className="ornament-symbol" />
        </div>
      ) : null}
      {showSocialSection ? (
        <section className="social-section">
          <p className="section-label">Join the Community</p>
          <div className="social-links">
            <a
              className="social-btn"
              href="https://www.facebook.com/profile.php?id=100092543996090"
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
              </svg>
              Facebook
            </a>
            <a
              className="social-btn"
              href="https://www.instagram.com/play_dfiance/"
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path
                  d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"
                  fill="#0a0806"
                />
                <line
                  x1="17.5"
                  y1="6.5"
                  x2="17.51"
                  y2="6.5"
                  stroke="#0a0806"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Instagram
            </a>
            <a
              className="social-btn"
              href="https://x.com/Dfiancegame"
              target="_blank"
              rel="noreferrer"
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Twitter / X
            </a>
            <a
              className="social-btn"
              href={DISCORD_URL}
              target={DISCORD_URL.startsWith("http") ? "_blank" : undefined}
              rel={DISCORD_URL.startsWith("http") ? "noreferrer" : undefined}
            >
              <svg viewBox="0 0 24 24" aria-hidden>
                <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.013.043.031.056a19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892a.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z" />
              </svg>
              Discord
            </a>
          </div>
        </section>
      ) : null}

      <footer className="footer-strip">
        <nav className="footer-legal" aria-label="Site">
          {variant === "home" ? (
            <Link to="/faq">FAQ</Link>
          ) : (
            <Link to="/">Home</Link>
          )}
          {variant !== "privacy" ? <Link to="/privacy-policy">Privacy</Link> : null}
          {variant !== "terms" ? <Link to="/terms">Terms</Link> : null}
        </nav>
        <div className="footer-brand-row">
          <img className="footer-logo-small" src={logo} alt="Dfiance" />
          <span className="footer-copy">
            © 2026 Dfiance: Dominance · All rights reserved
          </span>
        </div>
      </footer>
    </>
  );
};

export default JoinCommunityAndFooter;
